import { Button } from "src/components/buttons/shadcn-button";
import { TaskListRwo } from "./types/dailyTaskRwo";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate, initials } from "src/utils";

interface TaskTableRwoProps {
  tasks: TaskListRwo[];
  page: number;
  total: number;
  pagesize: number;
  nextPage: () => void;
  prevPage: () => void;
  selectPage: (page: number) => void;
  selectTask: (task: TaskListRwo) => void;
}

const TaskTableRwo = ({
  tasks,
  page,
  total,
  pagesize,
  nextPage,
  prevPage,
  selectPage,
  selectTask
}: TaskTableRwoProps) => {
  const pages = Math.max(1, Math.ceil(total / pagesize));

  const first = total === 0 ? 0 : (page - 1) * pagesize + 1;
  const last = Math.min(page * pagesize, total);

  return (
    <div>
      <div className="relative">
        <table className="w-full border-collapse whitespace-nowrap text-left max-[760px]:min-w-[710px] [&_th]:h-[43px] [&_th]:border-y [&_th]:border-border [&_th]:bg-muted [&_th]:px-[17px] [&_th]:text-[10px] [&_th]:font-medium [&_th]:text-white [&_td]:h-[50px] [&_td]:border-b [&_td]:border-border [&_td]:px-[17px] [&_td]:py-1 [&_td]:text-xs min-[1500px]:[&_td]:h-[50px] [&_tr:last-child_td]:border-b-0 [&_th:last-child]:w-[35px] [&_th:last-child]:pr-1 [&_td:last-child]:pr-1">
          <thead>
            <tr>
              <th>Employee code</th>
              <th>Employee Name</th>
              <th>Submitted on</th>
              <th>
                <span className="sr-only">View details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => {
              return (
                <tr
                  key={task.id}
                  tabIndex={0}
                  className="cursor-pointer transition-colors duration-150 hover:bg-gray-200 focus-visible:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gray-300 motion-reduce:transition-none"
                  aria-label={`View report for ${task?.employee_name ?? `Employee ${task.employee_id}`} on ${formatDate(task.date)}`}
                  onClick={() => selectTask(task)}
                >
                  <td>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {task?.employee_code}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-[11px]">
                      <span className="grid size-[33px] shrink-0 place-items-center rounded-full text-[10px] font-semibold bg-violet-50 text-violet-600">
                        {initials(task?.employee_name)}
                      </span>
                      <span className="font-[550]">{task?.employee_name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-col gap-1 text-[11px]">
                      <span>{formatDate(task.created_at || task.date)}</span>
                      <span className="text-[9px] text-muted-foreground">
                        {task.created_at
                          ? new Date(task.created_at).toLocaleTimeString(
                              "en-IN",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                timeZone: "Asia/Kolkata",
                              },
                            )
                          : "—"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground"
                      aria-label={`Open ${task?.employee_name ?? "employee"} report for ${formatDate(task.date)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectTask(task);
                      }}
                    >
                      <ArrowUpRight />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-[15px] border-t border-border py-[19px] max-[760px]:flex-wrap">
        <div className="text-[10px] text-muted-foreground">
          Showing{" "}
          <strong className="">
            {first}–{last}
          </strong>{" "}
          of <strong className="">{total}</strong> submissions
        </div>

        <div className="flex items-center gap-[5px] max-[760px]:ml-auto [&_button]:size-[29px] [&_button]:text-[11px]">
          <Button
            variant="outline"
            size="icon"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={prevPage}
          >
            <ChevronLeft />
          </Button>

          {Array.from({ length: pages }, (_, index) => index + 1)
            .filter(
              (pageNumber) =>
                pageNumber === 1 ||
                pageNumber === pages ||
                Math.abs(pageNumber - page) <= 1,
            )
            .map((pageNumber) => (
              <Button
                key={pageNumber}
                variant={pageNumber === page ? "default" : "ghost"}
                size="icon"
                aria-current={pageNumber === page ? "page" : undefined}
                aria-label={`Page ${pageNumber}`}
                onClick={() => selectPage(pageNumber)}
              >
                {pageNumber}
              </Button>
            ))}

          <Button
            variant="outline"
            size="icon"
            aria-label="Next page"
            disabled={page >= pages}
            onClick={nextPage}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskTableRwo;
